// -------- Define custom elements --------

import './component-oembed.js'

// -------- Initialize Swiper slider for Gallery blocks --------

// NB: Swiper is bundled with the theme, so it may be somewhat outdated.

function initializeSanityGalleries() {
    const SELECTORS = {
        gallery: '.gallery',
        sequentialLazyGallery: '.gallery[data-gallery-lazy-mode="sequential"]',
        slider: '.gallery .swiper',
        config: '[data-init]',
        nextButton: '.swiper-arrow-next',
        previousButton: '.swiper-arrow-prev',
        pagination: '.swiper-pagination',
        lazyImage: 'img[loading="lazy"]',
    };

    function toNumber(value, fallback = 0) {
        const number = Number.parseFloat(value);
        return Number.isFinite(number) ? number : fallback;
    }

    function hasValue(value) {
        return value !== undefined && value !== null && value !== '';
    }

    function toBoolean(value, fallback = false) {
        if (value === true || value === 'true') return true;
        if (value === false || value === 'false' || value === 'onwaar') return false;
        return fallback;
    }

    function sortDesktopFirstBreakpoints(settings) {
        return [...(settings.breakpoints || [])]
            .filter((breakpoint) => Number.isFinite(toNumber(breakpoint.breakpoint, NaN)))
            .sort((a, b) => toNumber(a.breakpoint) - toNumber(b.breakpoint));
    }

    function mergePadding(basePadding = {}, overridePadding = {}) {
        basePadding = basePadding || {};
        overridePadding = overridePadding || {};

        return {
            top: hasValue(overridePadding.top) ? overridePadding.top : basePadding.top,
            right: hasValue(overridePadding.right) ? overridePadding.right : basePadding.right,
            bottom: hasValue(overridePadding.bottom) ? overridePadding.bottom : basePadding.bottom,
            left: hasValue(overridePadding.left) ? overridePadding.left : basePadding.left,
        };
    }

    function mergeResponsiveSettings(baseSettings, overrideSettings = {}) {
        const mergedSettings = { ...baseSettings };

        Object.entries(overrideSettings || {}).forEach(([key, value]) => {
            if (key === 'padding' || !hasValue(value)) return;
            mergedSettings[key] = value;
        });

        mergedSettings.padding = mergePadding(baseSettings.padding, overrideSettings?.padding);

        return mergedSettings;
    }

    function getMatchedDesktopFirstBreakpoint(settings) {
        return sortDesktopFirstBreakpoints(settings)
            .find((breakpoint) => window.matchMedia(`(max-width: ${toNumber(breakpoint.breakpoint)}px)`).matches);
    }

    function getSmallestViewportSettings(settings) {
        const breakpoints = sortDesktopFirstBreakpoints(settings);
        return breakpoints.length > 0 ? mergeResponsiveSettings(settings, breakpoints[0]) : settings;
    }

    function getCurrentPadding(settings) {
        const breakpoint = getMatchedDesktopFirstBreakpoint(settings);
        const padding = mergePadding(settings.padding, breakpoint?.padding);

        return {
            top: toNumber(padding.top),
            right: toNumber(padding.right),
            bottom: toNumber(padding.bottom),
            left: toNumber(padding.left),
        };
    }

    function applyGalleryPadding(slider, settings) {
        const gallery = slider.closest(SELECTORS.gallery);
        if (!gallery) return;

        const padding = getCurrentPadding(settings);
        gallery.style.setProperty('--gallery-pad-top', `${padding.top}px`);
        gallery.style.setProperty('--gallery-pad-right', `${padding.right}px`);
        gallery.style.setProperty('--gallery-pad-bottom', `${padding.bottom}px`);
        gallery.style.setProperty('--gallery-pad-left', `${padding.left}px`);
    }

    function scheduleIdle(callback) {
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(callback, { timeout: 1200 });
            return;
        }

        window.setTimeout(callback, 1);
    }

    function isLoop(settings) {
        return (settings.type || 'slide') === 'loop';
    }

    function isPaginationEnabled(settings) {
        return !isLoop(settings) && toBoolean(settings.pagination, true);
    }

    function getNavigationOptions(slider, settings) {
        if (!toBoolean(settings.arrows, true)) return false;

        return {
            nextEl: slider.querySelector(SELECTORS.nextButton),
            prevEl: slider.querySelector(SELECTORS.previousButton),
        };
    }

    function getPaginationOptions(slider, settings) {
        if (!isPaginationEnabled(settings)) return false;

        return {
            el: slider.querySelector(SELECTORS.pagination),
            type: 'bullets',
            clickable: true,
        };
    }

    function getSlidesPerView(settings) {
        return toBoolean(settings.autoWidth) ? 'auto' : toNumber(settings.perPage, 1);
    }

    function canUseNativeFade(settings) {
        if ((settings.type || 'slide') !== 'fade') return false;

        const responsiveSettings = [
            settings,
            ...sortDesktopFirstBreakpoints(settings).map((breakpoint) => mergeResponsiveSettings(settings, breakpoint)),
        ];

        return responsiveSettings.every((responsiveSetting) => (
            !toBoolean(responsiveSetting.autoWidth)
            && toNumber(responsiveSetting.perPage, 1) <= 1
        ));
    }

    function toResponsiveSwiperOptions(settings, slider) {
        return {
            allowTouchMove: toBoolean(settings.drag, true),
            navigation: getNavigationOptions(slider, settings),
            pagination: getPaginationOptions(slider, settings),
            rewind: !isLoop(settings) && toBoolean(settings.rewind),
            slidesPerGroup: toNumber(settings.perMove, 1),
            slidesPerView: getSlidesPerView(settings),
            spaceBetween: toNumber(settings.gap),
            speed: toNumber(settings.speed, 400),
        };
    }

    function getSwiperBreakpoints(settings, slider) {
        const breakpoints = sortDesktopFirstBreakpoints(settings);
        if (breakpoints.length === 0) return {};

        // Sanity breakpoints are max-width rules; Swiper breakpoints are min-width rules.
        return Object.fromEntries(breakpoints.map((breakpoint, index) => {
            const nextBreakpoint = breakpoints[index + 1];
            const rangeSettings = nextBreakpoint
                ? mergeResponsiveSettings(settings, nextBreakpoint)
                : settings;

            return [
                toNumber(breakpoint.breakpoint) + 1,
                toResponsiveSwiperOptions(rangeSettings, slider),
            ];
        }));
    }

    function getSwiperOptions(settings, slider) {
        const smallestViewportSettings = getSmallestViewportSettings(settings);
        const useNativeFade = canUseNativeFade(settings);

        return {
            ...toResponsiveSwiperOptions(smallestViewportSettings, slider),
            autoHeight: toBoolean(settings.autoHeight),
            autoplay: toBoolean(settings.autoplay) && {
                delay: toNumber(settings.autoplayInterval, 4000),
                disableOnInteraction: false,
            },
            effect: useNativeFade ? 'fade' : 'slide',
            fadeEffect: useNativeFade ? { crossFade: true } : undefined,
            freeMode: false,
            grabCursor: true,
            initialSlide: toNumber(settings.start),
            keyboard: true,
            lazyPreloadPrevNext: settings.lazyLoad === 'nearby' ? 1 : 0,
            loop: isLoop(settings),
            roundLengths: true,
            watchOverflow: toBoolean(settings.watchOverflow, true),
            breakpoints: getSwiperBreakpoints(settings, slider),
        };
    }

    function parseGalleryConfig(slider) {
        const configElement = slider.querySelector(SELECTORS.config);
        if (!configElement) return null;

        try {
            return JSON.parse(configElement.textContent);
        } catch (error) {
            console.error('Cannot parse gallery Swiper settings.', error);
            return null;
        }
    }

    function initializeAutoplayPauses(slider, swiper, settings) {
        if (!swiper.autoplay || !toBoolean(settings.autoplay)) return;

        const pause = () => swiper.autoplay.stop();
        const resume = () => swiper.autoplay.start();

        if (toBoolean(settings.pauseOnHover)) {
            slider.addEventListener('mouseenter', pause);
            slider.addEventListener('mouseleave', resume);
        }

        if (toBoolean(settings.pauseOnFocus)) {
            slider.addEventListener('focusin', pause);
            slider.addEventListener('focusout', (event) => {
                if (slider.contains(event.relatedTarget)) return;
                resume();
            });
        }
    }

    function initializeSequentialLazyload(gallery) {
        const images = [...gallery.querySelectorAll(SELECTORS.lazyImage)];
        let imageIndex = 0;

        const loadNext = () => {
            const image = images[imageIndex];
            imageIndex += 1;
            if (!image) return;

            if (image.complete) {
                scheduleIdle(loadNext);
                return;
            }

            image.addEventListener('load', () => scheduleIdle(loadNext), { once: true });
            image.addEventListener('error', () => scheduleIdle(loadNext), { once: true });
            image.loading = 'eager';
        };

        scheduleIdle(loadNext);
    }

    function initializeSlider(slider) {
        const settings = parseGalleryConfig(slider);
        if (!settings) return;

        applyGalleryPadding(slider, settings);
        window.addEventListener('resize', () => applyGalleryPadding(slider, settings));

        const swiper = new Swiper(slider, getSwiperOptions(settings, slider));
        initializeAutoplayPauses(slider, swiper, settings);

        // Disabled arrows must still capture clicks so they don't hit product links underneath.
        slider.querySelectorAll(`${SELECTORS.nextButton}, ${SELECTORS.previousButton}`).forEach((button) => {
            button.addEventListener('click', (event) => {
                if (
                    button.classList.contains('swiper-button-disabled')
                    || button.classList.contains('swiper-button-lock')
                ) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            });
        });
    }

    document.querySelectorAll(SELECTORS.sequentialLazyGallery).forEach(initializeSequentialLazyload);
    document.querySelectorAll(SELECTORS.slider).forEach(initializeSlider);
}

window.addEventListener('load', initializeSanityGalleries);


// -------- Shoppable Look / Look Sidebar Interaction --------

window.addEventListener('load', () => {
    document.querySelectorAll('[data-shoppable-look]').forEach((wrapper) => {
        const hotspots = wrapper.querySelectorAll('.hotspot');
        const lookItems = wrapper.querySelectorAll('.look-items > li');

        hotspots.forEach((hotspot) => {
            hotspot.addEventListener("click", () => {
                hotspots.forEach((other) => {
                    other.classList.remove('on');
                });
                hotspot.classList.add('on');

                const hotspotIndex = hotspot.dataset.n;
                lookItems.forEach((lookItem) => {
                    if (lookItem.dataset.n === hotspotIndex) {
                        lookItem.classList.add('on');
                    } else {
                        lookItem.classList.remove('on');
                    }
                });
            });
        });
    });
});


// -------- JS Portable text rendering --------

// JS Portable text rendering is a backup mechanism. Use it only if Liquid fails.

// import { toHTML } from "./portabletext.bundle.js"

// const parserOptions = {
//   components: {
//     types: {
//       pullQuote: ({ value }) => {
//         return `<div class="pull-quote">
//           <p>${value.quote}</p>
//           <span>— ${value.attribution}, ${value.role}</span>
//         <div>`
//       }
//     },
//   },
// };

// document.querySelectorAll('[data-sanity-content-json]').forEach((dataTag) => {
//   if (dataTag.getAttribute('rendered')) return;
//   dataTag.setAttribute('rendered', true);
//   let contentArray = [];
//   try {
//     contentArray = JSON.parse(dataTag.textContent);
//   } catch (e) {
//     console.error(`Cannot parse JSON at data script ${dataTag}`);
//     console.error(e);
//     return;
//   }

//   const contentHtml = toHTML(contentArray, parserOptions);
//   dataTag.insertAdjacentHTML('afterend', contentHtml);
// });
